import React, { useEffect, useState, useCallback } from 'react';
import { Trash2, Upload } from 'lucide-react';
import { GRADIENTS, DEFAULT_BACKGROUND, DEFAULT_TEXT_EFFECT, backgroundCss, textEffectCss } from '../display/themes';
import { isMediaStoreSupported, listImages, saveImage, deleteImage, onImagesChanged } from '../services/mediaStore';
import { markImageDeleted } from '../services/backgroundSync';
import useBackgroundImage from '../hooks/useBackgroundImage';

const TABS = [
  { id: 'color', label: 'Colour' },
  { id: 'gradient', label: 'Gradient' },
  { id: 'image', label: 'Image' },
];

const Thumb = ({ id, selected, onSelect, onDelete, name }) => {
  const { url } = useBackgroundImage(id);
  return (
    <div className={`relative rounded-lg overflow-hidden border-2 ${selected ? 'border-amber-400' : 'border-transparent'}`}>
      <button
        type="button"
        onClick={onSelect}
        aria-label={`Use ${name}`}
        className="block w-full h-14 bg-slate-700 bg-cover bg-center"
        style={url ? { backgroundImage: `url("${url}")` } : undefined}
      />
      <button
        type="button"
        onClick={onDelete}
        aria-label={`Delete ${name}`}
        className="absolute top-0.5 right-0.5 p-0.5 rounded bg-black/60 text-white hover:bg-red-600"
      >
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
};

/**
 * Background + text-effect controls for the projector. `settings` is the full
 * settings object; `onChange` receives the updated object.
 */
const ThemeSettings = ({ settings, onChange, dm, t }) => {
  const background = { ...DEFAULT_BACKGROUND, ...settings.background };
  const textEffect = { ...DEFAULT_TEXT_EFFECT, ...settings.textEffect };
  const supported = isMediaStoreSupported();
  const [images, setImages] = useState([]);
  const [error, setError] = useState('');
  const { url: selectedUrl } = useBackgroundImage(background.type === 'image' ? background.imageId : null);

  const setBackground = (patch) => onChange({ ...settings, background: { ...background, ...patch } });
  const setEffect = (patch) => onChange({ ...settings, textEffect: { ...textEffect, ...patch } });

  const refresh = useCallback(async () => {
    if (!supported) return;
    try {
      setImages(await listImages());
    } catch {
      setImages([]);
    }
  }, [supported]);

  useEffect(() => { refresh(); }, [refresh]);
  // Pick up images that arrive from another computer.
  useEffect(() => onImagesChanged(refresh), [refresh]);

  const onUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    try {
      const saved = await saveImage(file);
      await refresh();
      setBackground({ type: 'image', imageId: saved.id });
    } catch (err) {
      setError(err.message || 'Could not save the image');
    }
  };

  const onDelete = async (id) => {
    markImageDeleted(id); // so the cloud copy is removed too, and not downloaded again
    await deleteImage(id).catch(() => {});
    await refresh();
    if (background.imageId === id) setBackground({ type: 'color', imageId: null });
  };

  const preview = backgroundCss(background, settings.backgroundColor, selectedUrl);
  const label = `block text-xs font-bold mb-2 ${t.textPrimary}`;
  const tabBtn = (active) => `flex-1 px-2 py-1 rounded-lg text-xs font-semibold ${active
    ? 'bg-amber-500 text-slate-900'
    : (dm ? 'bg-slate-700 text-slate-200' : 'bg-gray-200 text-gray-700')}`;

  return (
    <div className="mb-4 space-y-3">
      <div>
        <label className={label}>Background</label>
        <div className="flex gap-1" role="tablist">
          {TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={background.type === tab.id}
              className={tabBtn(background.type === tab.id)}
              onClick={() => setBackground({ type: tab.id })}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {background.type === 'color' && (
        <input
          type="color"
          aria-label="Background colour"
          value={settings.backgroundColor}
          onChange={(e) => onChange({ ...settings, backgroundColor: e.target.value })}
          className={`w-full h-10 rounded-lg cursor-pointer border ${dm ? 'border-slate-700' : 'border-gray-300'}`}
        />
      )}

      {background.type === 'gradient' && (
        <div className="grid grid-cols-4 gap-1.5">
          {GRADIENTS.map(g => (
            <button
              key={g.id}
              type="button"
              title={g.name}
              aria-label={`${g.name} gradient`}
              onClick={() => setBackground({ gradientId: g.id })}
              className={`h-10 rounded-lg border-2 ${background.gradientId === g.id ? 'border-amber-400' : 'border-transparent'}`}
              style={{ background: g.css }}
            />
          ))}
        </div>
      )}

      {background.type === 'image' && (
        supported ? (
          <div className="space-y-2">
            <label className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-brand-600 text-slate-50 hover:bg-brand-500 cursor-pointer">
              <Upload className="w-3 h-3" /> Upload image
              <input type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={onUpload} />
            </label>
            {error && <p role="alert" className="text-xs text-red-400">{error}</p>}
            {images.length === 0 ? (
              <p className={`text-xs ${t.textSecondary}`}>No images yet. PNG, JPEG or WebP up to 10 MB; large photos are resized to 1920x1080.</p>
            ) : (
              <div className="grid grid-cols-3 gap-1.5">
                {images.map(img => (
                  <Thumb
                    key={img.id}
                    id={img.id}
                    name={img.name}
                    selected={background.imageId === img.id}
                    onSelect={() => setBackground({ imageId: img.id })}
                    onDelete={() => onDelete(img.id)}
                  />
                ))}
              </div>
            )}
            <div>
              <label className={label}>Dim image: {Math.round(background.dim * 100)}%</label>
              <input
                type="range" min="0" max="0.8" step="0.05" value={background.dim}
                onChange={(e) => setBackground({ dim: parseFloat(e.target.value) })}
                className="w-full h-2 bg-blue-950 rounded-lg appearance-none cursor-pointer accent-amber-500"
              />
            </div>
            <p className={`text-xs ${t.textSecondary}`}>Images stay on this computer.</p>
          </div>
        ) : (
          <p className={`text-xs ${t.textSecondary}`}>
            Image backgrounds need browser storage, which is not available here (for example in a private window).
          </p>
        )
      )}

      <div>
        <label className={label}>Text shadow: {textEffect.shadow === 0 ? 'Off' : textEffect.shadow}</label>
        <input
          type="range" min="0" max="3" step="1" value={textEffect.shadow}
          onChange={(e) => setEffect({ shadow: parseInt(e.target.value, 10) })}
          className="w-full h-2 bg-blue-950 rounded-lg appearance-none cursor-pointer accent-amber-500"
        />
        <label className="flex items-center gap-2 mt-2 text-xs cursor-pointer">
          <input type="checkbox" checked={textEffect.outline} onChange={(e) => setEffect({ outline: e.target.checked })} />
          <span className={t.textPrimary}>Text outline</span>
        </label>
      </div>

      <div
        data-testid="theme-sample"
        className="relative rounded-lg overflow-hidden h-20 flex items-center justify-center"
        style={{ ...preview.style, color: settings.fontColor, fontFamily: settings.fontFamily }}
      >
        {preview.dim > 0 && <div className="absolute inset-0" style={{ background: `rgba(0,0,0,${preview.dim})` }} />}
        <span className="relative text-lg font-bold" style={textEffectCss(textEffect)}>Sample text</span>
      </div>
    </div>
  );
};

export default ThemeSettings;
